import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// RFC 4648 base32 alphabet, case-insensitive, with optional '=' padding.
// The backend feeds this value to pyotp.TOTP(secret) which base32-decodes it;
// any other character raises "Non-base32 digit found".
const BASE32_PATTERN = /^[A-Za-z2-7]+=*$/;

// Yuki's base32 TOTP secret is always exactly 32 characters (a 160-bit key).
const EXPECTED_LENGTH = 32;

// Characters that ride along when a secret is copied from a PDF, a web page or an authenticator
// export: whitespace, zero-width characters and BOMs (which \s does not cover), and the hyphens
// some apps group the secret with.
const SEPARATORS = /[\s\u200B-\u200D\u2060\uFEFF-]/g;

/**
 * Reduce a pasted OTP secret to its bare base32 characters.
 *
 * Padding is dropped as well: pyotp accepts the secret without it, and it would otherwise push
 * a 32-character key past the length check.
 */
export function normalizeOtpSecret(value: string): string {
    return value.replace(SEPARATORS, '').replace(/=+$/, '').toUpperCase();
}

export function base32Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (!value) {
            return null;
        }

        const normalized = normalizeOtpSecret(String(value));

        if (!normalized) {
            return null;
        }

        // A purely numeric value is almost certainly the 6-digit TOTP result
        // entered by mistake instead of the base32 shared secret (which always
        // contains letters). Digits 2-7 are valid base32, so this must be
        // caught explicitly rather than by the alphabet check below.
        if (/^\d+$/.test(normalized)) {
            return { otpNumericCode: true };
        }

        if (!BASE32_PATTERN.test(normalized)) {
            return { base32Invalid: true };
        }

        if (normalized.length !== EXPECTED_LENGTH) {
            return { base32Length: { requiredLength: EXPECTED_LENGTH, actualLength: normalized.length } };
        }

        return null;
    };
}
